import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import api from '@/lib/api';

export default function SubscriptionRequired() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const message = localStorage.getItem('subscription_blocked') || 'Your subscription has expired. Please subscribe to continue.';

  useEffect(() => {
    // If not authenticated, redirect to login with return URL
    if (!authLoading && !user) {
      navigate('/login?redirect=/subscription-required');
    }
  }, [user, authLoading, navigate]);

  const handleSubscribe = async (planId) => {
    setLoading(true);
    setError('');
    try {
      const callbackUrl = `${window.location.origin}/payment-success`;
      const { data } = await api.post('/subscription/subscribe', { plan_id: planId, callback_url: callbackUrl });
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to initialize payment';
      if (detail.includes('authenticated')) {
        navigate('/login?redirect=/subscription-required');
      } else {
        setError(detail);
      }
    } finally { setLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('subscription_blocked');
    navigate('/login');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="subscription-required-page">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-lg w-full text-center">
        <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Subscription Required</h2>
        <p className="text-sm text-slate-500 mb-8">{message}</p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4 text-sm">{error}</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {/* Standard */}
          <div className="border border-slate-200 rounded-xl p-5 text-center">
            <h3 className="text-base font-bold text-slate-900 mb-1">Standard</h3>
            <p className="text-3xl font-extrabold text-slate-900 mb-1">R250<span className="text-sm text-slate-400 font-normal">/mo</span></p>
            <p className="text-xs text-slate-500 mb-4">Up to 5 properties</p>
            <Button
              className="w-full bg-slate-900 hover:bg-slate-800 text-white text-sm"
              onClick={() => handleSubscribe('standard')}
              disabled={loading}
              data-testid="subscribe-standard-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}Subscribe
            </Button>
          </div>

          {/* Pro */}
          <div className="border-2 border-slate-900 rounded-xl p-5 text-center relative">
            <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[9px] font-bold uppercase px-3 py-0.5 rounded-full">Popular</span>
            <h3 className="text-base font-bold text-slate-900 mb-1">Pro</h3>
            <p className="text-3xl font-extrabold text-slate-900 mb-1">R500<span className="text-sm text-slate-400 font-normal">/mo</span></p>
            <p className="text-xs text-slate-500 mb-4">Unlimited properties</p>
            <Button
              className="w-full bg-slate-900 hover:bg-slate-800 text-white text-sm"
              onClick={() => handleSubscribe('pro')}
              disabled={loading}
              data-testid="subscribe-pro-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}Subscribe
            </Button>
          </div>
        </div>

        <button onClick={handleLogout} className="text-xs text-slate-400 hover:text-slate-600 underline" data-testid="logout-btn">
          Sign out
        </button>
      </div>
    </div>
  );
}
